import { produce } from 'immer'
import { isEqual, isNil } from 'lodash'

import { formatTime } from '@/utils/helpers'
import { CHANNEL_TYPE } from '@/utils/constants'

// The scorecard fields that decide WHICH CALLS get sampled. The frontend mirror of the server's
// `_ELIGIBILITY_COLUMNS` (`routers/promptimizer.py`); the two answer the same question either side of a save and
// should be changed together.
//
// ⚠ The settings modal carries more than this — `required_score_count`, `scorecard_frequency`, fallback — and none
// of them change which calls a run measures, so none of them belong here. That is the whole point: a warning that
// fires on every settings edit is one nobody reads.
const SAMPLING_FIELDS = [
  'eligibility_prompt_id',
  'should_eligibility_respond_yes',
  'call_type',
  'min_duration_in_seconds',
  'max_duration_in_seconds',
  'min_message_count',
  'max_message_count',
  'multicall',
  'multicall_same_user',
  'ignore_scored_status',
  'dispositions_inclusive',
  'tags_inclusive',
  'users_inclusive',
  'voip_campaigns_inclusive',
]

// The list filters. Some arrive as [{id}] and some as bare values, and neither promises an order.
const SAMPLING_LISTS = [
  'config_cids',
  'manual_config_cids',
  'dispositions',
  'excluded_outcomes',
  'users',
  'tags',
  'voip_campaigns',
]

const listContents = (list) => (isNil(list) ? [] : list.map((item) => item?.id ?? item).sort())

/**
 * The sampling-deciding fields of a scorecard config, in a shape two configs can be compared by.
 *
 * Every key is present with an explicit null so a field the API omits compares equal to one it sends as null —
 * otherwise a config that simply didn't mention a filter would read as different from one that did.
 */
const samplingFingerprint = (config) => {
  const source = config ?? {}
  const fingerprint = {}

  SAMPLING_FIELDS.forEach((field) => {
    fingerprint[field] = source[field] ?? null
  })
  SAMPLING_LISTS.forEach((field) => {
    fingerprint[field] = listContents(source[field])
  })

  return fingerprint
}

const startOf7DaysAgo = formatTime(
  new Date(new Date().setDate(new Date().getDate() - 7)),
  'yyyy-MM-DDT00:00:00Z'
)

const endOfToday = formatTime(new Date(), 'yyyy-MM-DDT23:59:59Z')

// Action Types
export const SET_LOADING = 'qaCopilot/setLoading'
export const SET_ERROR = 'qaCopilot/setError'
export const SET_SCORECARD_CONFIG = 'qaCopilot/setScorecardConfig'
export const SET_SCORECARD_CONFIG_VERSIONS = 'qaCopilot/setScorecardConfigVersions'
export const SET_SCORECARD_CONFIG_CHANGES = 'qaCopilot/setScorecardConfigChanges'
export const EDIT_SCORECARD_CONFIG = 'qaCopilot/editScorecardConfig'
export const SET_TEMPORARY_CRITERIA = 'qaCopilot/setTemporaryCriteria'
export const SET_DATA = 'qaCopilot/setData'
export const SET_FILTER = 'qaCopilot/setFilter'
export const SET_DRAFT_FILTER = 'qaCopilot/setDraftFilter'
export const COMMIT_DRAFT_FILTERS = 'qaCopilot/commitDraftFilters'
export const RESET_DRAFT_FILTERS = 'qaCopilot/resetDraftFilters'
export const SET_ORGANIZATION = 'qaCopilot/setOrganization'
export const SET_SCOPE_OVERLAY = 'qaCopilot/setScopeOverlay'
export const UPDATE_SETTINGS = 'qaCopilot/updateSettings'
export const SET_FOCUS_AREA = 'qaCopilot/setFocusArea'
export const CLEAR_FILTERS = 'qaCopilot/clearFilters'

// Action Creators
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setError = (payload) => ({ type: SET_ERROR, payload })
export const setScorecardConfig = (payload) => ({ type: SET_SCORECARD_CONFIG, payload })
export const setScorecardConfigVersions = (payload) => ({
  type: SET_SCORECARD_CONFIG_VERSIONS,
  payload,
})
export const setScorecardConfigChanges = (payload) => ({
  type: SET_SCORECARD_CONFIG_CHANGES,
  payload,
})
export const editScorecardConfig = (payload) => ({ type: EDIT_SCORECARD_CONFIG, payload })
export const setTemporaryCriteria = (payload) => ({ type: SET_TEMPORARY_CRITERIA, payload })
export const setData = (payload) => ({ type: SET_DATA, payload })
export const setFilter = (payload) => ({ type: SET_FILTER, payload })
// Filter-bar writes go through setDraftFilter; they don't commit to
// `filters` (and therefore don't trigger dashboard re-fetches) until
// the user clicks Apply, which dispatches commitDraftFilters.
// Pickers + drilldowns continue to use setFilter directly for
// immediate scope changes.
export const setDraftFilter = (payload) => ({ type: SET_DRAFT_FILTER, payload })
export const commitDraftFilters = () => ({ type: COMMIT_DRAFT_FILTERS })
export const resetDraftFilters = () => ({ type: RESET_DRAFT_FILTERS })
export const setOrganization = (payload) => ({ type: SET_ORGANIZATION, payload })
// Scope-tab picker overlay: { filterKey: 'agents'|'scorecards'|'tags',
// entity: {value,label} | null } or null on Overview. Lives in redux
// (not just page state) so EVERY dashboard fetch path -- Apply
// Filters, org switch, day-click drills -- carries the active tab's
// focus narrowing instead of silently reverting to org-wide data.
export const setScopeOverlay = (payload) => ({ type: SET_SCOPE_OVERLAY, payload })
export const updateSettings = (payload) => ({ type: UPDATE_SETTINGS, payload })
export const setFocusArea = (payload) => ({ type: SET_FOCUS_AREA, payload })
export const clearFilters = () => ({ type: CLEAR_FILTERS })

// Shape shared by `filters` (applied; what widgets see) and
// `draftFilters` (in-progress; what the filter bar writes to until
// the user clicks Apply). Keeping them as separate slices means a
// pending edit doesn't trigger any dashboard refetches.
const buildDefaultFilters = () => ({
  organizationId: null,
  scorecards: [],
  agents: [],
  tags: [],
  playbooks: [],
  dispositions: { selected: [], includes: true },
  voipCampaigns: [],
  isWin: null,
  scoredStatus: null,
  startDate: startOf7DaysAgo,
  endDate: endOfToday,
  scorecard_type: 'copilot',
  callType: CHANNEL_TYPE.CALL,
})

// State
export const initialState = {
  filters: buildDefaultFilters(),
  draftFilters: buildDefaultFilters(),
  scopeOverlay: null,
  data: {
    organizations: [],
    scorecards: [],
    agents: [],
    tags: [],
    playbooks: [],
    voipCampaigns: [],
    allVoipCampaigns: [],
    activeVoipCampaigns: [],
    nonCompliantCriteria: [],
    nonCompliantAgents: [],
    failedCallData: null,
    failedCriteriaCallData: null,
    evaluatedCallData: null,
    coachingOpportunities: [],
    topInfractors: [],
    reporting: {
      aggregate_scores: { total: 0 },
      scorecard_counts: { total: 0 },
      call_counts: { total: 0 },
      filters: {},
      scorecard_configs: [],
    },
    reportingByDay: { aggregates: [], calls: [] },
    reportingByUser: {
      aggregate_scores_by_user: [],
      scorecard_counts: null,
      call_counts: null,
      filters: {},
    },
    enterpriseScorecardPerformance: [],
    enterpriseOrganizationPerformance: {},
    enterpriseEvaluatedCallData: {},
    enterpriseFailedCallData: {},
    enterpriseOverallAverageScore: null,
    enterpriseScoresPerOrganization: [],
    scorecardPerformance: {},
    criteriaTrends: null,
    // Prior-window by_date rollup. Populated by the bootstrap action
    // so OverviewCardsRow + ScoreTrendsWidget can read it instead of
    // each firing a separate prior-window fetch.
    reportingByDayPrior: null,
    // Shared cohort dataset for the active scope tab (By Scorecard /
    // Campaign / Agent). Populated by `fetchCohortByScope`; consumed
    // by every comparative widget via `useCohortContext`. Shape:
    //   { scope, filtersHash, raw }
    // `filtersHash` is the cache key -- the dispatcher no-ops when
    // it already matches state. Null until a scope tab is active.
    cohort: null,
    // Bundled scorecard-overview slices (TREN-317 perf). Populated by
    // `fetchScorecardOverview` in a single round trip; widgets that
    // previously fetched these endpoints individually now read from
    // these redux keys.
    criteriaCorrelation: null,
    scorecardOverviewSid: null,
    appliedScorecards: [],
  },
  scorecardConfig: null,
  scorecardConfigVersions: [],
  scorecardConfigChanges: [],
  temporaryCriteria: null,
  loading: {
    scorecards: false,
    agents: false,
    tags: false,
    playbooks: false,
    voipCampaigns: false,
    allVoipCampaigns: false,
    scorecardConfig: false,
    scorecardConfigVersions: false,
    scorecardConfigChanges: false,
    copilotTestPrompt: false,
    nonCompliantCriteria: false,
    nonCompliantAgents: false,
    failedCallData: false,
    failedCriteriaCallData: false,
    evaluatedCallData: false,
    coachingOpportunities: false,
    topInfractors: false,
    enterpriseScorecardPerformance: false,
    enterpriseOrganizationPerformance: false,
    enterpriseEvaluatedCallData: false,
    enterpriseFailedCallData: false,
    enterpriseOverallAverageScore: false,
    enterpriseScoresPerOrganization: false,
    criteriaTrends: false,
    criteriaCorrelation: false,
    scorecardOverview: false,
    reportingByDayPrior: false,
    cohort: false,
    // Hash of the cohort fetch that's currently in flight (or null
    // when nothing is fetching). When multiple widgets dispatch the
    // same fetch in the same tick, the second dispatcher sees this
    // hash already set and bails -- no duplicate request.
    cohortInFlightHash: null,
  },
  error: {
    scorecardConfig: false,
  },
  dirty: false,
  // Whether the unsaved settings would change WHICH CALLS a run samples — see `samplingFingerprint`. Prompt
  // optimization measures against the SAVED config, so while these differ its numbers describe a different set of
  // calls than the screen implies, and it says so.
  //
  // A COMPARISON AGAINST THE SAVED CONFIG, not a "the modal was submitted" latch, so editing a value and putting
  // it back clears it again. `dirty` cannot carry this warning: it is also true after adding a criterion or
  // renaming the scorecard, neither of which changes which calls we measure.
  settingsDirty: false,
  // The saved config's sampling fields, kept so `settingsDirty` has a baseline to compare against — once editing
  // starts, `scorecardConfig` is the draft and the saved values are otherwise gone.
  savedSampling: samplingFingerprint(null),
  focusArea: '',
}

// Reducer
export default function qaCopilotReducer(state = initialState, action) {
  return produce(state, (newState) => {
    const { payload } = action

    switch (action.type) {
      case SET_LOADING:
        newState.loading = { ...state.loading, ...payload }
        break
      case SET_ERROR:
        newState.error = { ...state.error, ...payload }
        break
      case SET_SCORECARD_CONFIG:
        newState.scorecardConfig = payload
        // Both a load and a save land here, and either one makes what is on screen the saved truth.
        newState.savedSampling = samplingFingerprint(payload)
        newState.dirty = false
        newState.settingsDirty = false
        newState.error.scorecardConfig = false
        break
      case SET_SCORECARD_CONFIG_VERSIONS:
        newState.scorecardConfigVersions = payload
        break
      case SET_SCORECARD_CONFIG_CHANGES:
        newState.scorecardConfigChanges = payload
        break
      case EDIT_SCORECARD_CONFIG:
        newState.scorecardConfig = payload
        newState.dirty = true
        break
      case UPDATE_SETTINGS: {
        const merged = { ...state.scorecardConfig, ...payload }

        newState.scorecardConfig = merged
        newState.dirty = true
        // Against the SAVED baseline rather than the previous draft, so putting a value back clears the warning
        // instead of leaving it on until the next save.
        newState.settingsDirty = !isEqual(state.savedSampling, samplingFingerprint(merged))
        break
      }
      case SET_TEMPORARY_CRITERIA:
        newState.temporaryCriteria = payload
        newState.dirty = true
        break
      case SET_DATA:
        newState.data = { ...state.data, ...payload }
        break
      case SET_FILTER:
        newState.filters = { ...state.filters, ...payload }
        // Keep drafts in sync when something OUTSIDE the filter bar
        // mutates filters directly (pickers, drilldowns, restoring
        // saved filters). Without this, an immediate scope change
        // would leave the filter bar showing stale drafts.
        newState.draftFilters = { ...state.draftFilters, ...payload }
        break
      case SET_DRAFT_FILTER:
        newState.draftFilters = { ...state.draftFilters, ...payload }
        break
      case COMMIT_DRAFT_FILTERS:
        // Apply button: snapshot drafts onto filters. The dashboard
        // re-fetch is dispatched separately by the action creator;
        // this reducer only touches state.
        newState.filters = { ...state.draftFilters }
        break
      case RESET_DRAFT_FILTERS:
        // Discard pending changes -- drafts go back to whatever's
        // currently applied. Used by the filter bar's "Clear" /
        // "Cancel" surfaces.
        newState.draftFilters = { ...state.filters }
        break
      case CLEAR_FILTERS: {
        const cleared = {
          ...initialState.filters,
          organizationId: state.filters.organizationId,
          callType: CHANNEL_TYPE.CALL,
        }
        newState.filters = cleared
        newState.draftFilters = cleared
        break
      }
      case SET_ORGANIZATION: {
        const cleared = {
          ...state.filters,
          organizationId: action.payload || null, // also serves as a reset
          scorecards: [],
          agents: [],
          tags: [],
          playbooks: [],
          dispositions: { selected: [], includes: true },
          voipCampaigns: [],
        }
        newState.filters = cleared
        newState.draftFilters = cleared
        // The picked entity belongs to the previous org -- clear the
        // overlay so the org-switch refetch isn't scoped to it. The
        // pickers auto-replace stale picks and re-sync the overlay.
        newState.scopeOverlay = null
        newState.data = {
          ...state.data,
          scorecards: [],
          agents: [],
          tags: [],
          playbooks: [],
          dispositions: [],
          voipCampaigns: [],
          allVoipCampaigns: [],
          activeVoipCampaigns: [],
        }
        break
      }
      case SET_SCOPE_OVERLAY:
        newState.scopeOverlay = payload || null
        break
      case SET_FOCUS_AREA:
        newState.focusArea = payload
        break
      default:
        break
    }
  })
}
